import { useState, type ReactNode } from 'react';
import { Check, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';

export interface TaxonomyPickerItem {
  id: string;
  name: string;
  memberCount: number;
}

export interface TaxonomyPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** How many agents the choice will apply to. */
  agentCount: number;
  title: string;
  /** Explains both the existing-item and create-new paths. */
  description: string;
  icon: ReactNode;
  items: TaxonomyPickerItem[];
  /**
   * Entries every target agent already carries. They are shown as the current
   * choice and cannot be picked, because doing so would write nothing.
   */
  currentIds?: string[];
  /** Apply an existing entry. Should close the dialog on success. */
  onSelect: (id: string) => void;
  /** Create `name` and apply it. Should close the dialog on success. */
  onCreate: (name: string) => void;
  /** A write is in flight; the list stops accepting picks. */
  pending: boolean;
  /** Longest name the create action will accept. */
  maxNameLength: number;
  /** Shown when there is nothing to pick from yet. */
  emptyHint: string;
  searchPlaceholder: string;
  /** Accessible name of the search field, and the testid prefix. */
  searchLabel: string;
  testIdPrefix: string;
}

/**
 * The shared picker behind "move to group" and "add label": one search field
 * that filters what already exists and, when what you typed isn't one of
 * them, offers to create it.
 *
 * Both taxonomies get the same control on purpose. They differ in who
 * normally applies them — a person files a group, the classifier applies a
 * label — but when a person is doing it by hand the decision is identical:
 * pick from the vocabulary, or extend it.
 */
export function TaxonomyPickerDialog({
  open,
  onOpenChange,
  agentCount,
  title,
  description,
  icon,
  items,
  currentIds,
  onSelect,
  onCreate,
  pending,
  maxNameLength,
  emptyHint,
  searchPlaceholder,
  searchLabel,
  testIdPrefix,
}: TaxonomyPickerDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 sm:max-w-md" data-testid={`${testIdPrefix}-dialog`}>
        {/* The body is its own component so its search box lives and dies with
            the dialog content Radix unmounts on close: each opening starts
            clean without an effect reaching in to reset it. */}
        <PickerBody
          agentCount={agentCount}
          title={title}
          description={description}
          icon={icon}
          items={items}
          currentIds={currentIds}
          onSelect={onSelect}
          onCreate={onCreate}
          pending={pending}
          maxNameLength={maxNameLength}
          emptyHint={emptyHint}
          searchPlaceholder={searchPlaceholder}
          searchLabel={searchLabel}
          testIdPrefix={testIdPrefix}
        />
      </DialogContent>
    </Dialog>
  );
}

function PickerBody({
  agentCount,
  title,
  description,
  icon,
  items,
  currentIds,
  onSelect,
  onCreate,
  pending,
  maxNameLength,
  emptyHint,
  searchPlaceholder,
  searchLabel,
  testIdPrefix,
}: Omit<TaxonomyPickerDialogProps, 'open' | 'onOpenChange'>) {
  const [search, setSearch] = useState('');
  const current = new Set(currentIds ?? []);
  const trimmed = search.trim();
  const canCreate =
    trimmed.length > 0 &&
    trimmed.length <= maxNameLength &&
    !items.some((item) => item.name.toLowerCase() === trimmed.toLowerCase());

  return (
    <>
      <DialogHeader className="px-4 pt-4">
        <DialogTitle className="flex items-center gap-2 text-base">
          {icon}
          {title}
        </DialogTitle>
        <DialogDescription>
          {description} {agentCount === 1 ? '1 agent selected.' : `${agentCount} agents selected.`}
        </DialogDescription>
      </DialogHeader>
      <Command shouldFilter={false} className="bg-transparent">
        <CommandInput
          value={search}
          onValueChange={setSearch}
          placeholder={searchPlaceholder}
          aria-label={searchLabel}
        />
        <CommandList className="max-h-64">
          {items.length === 0 && !canCreate ? <CommandEmpty>{emptyHint}</CommandEmpty> : null}
          <CommandGroup>
            {items
              .filter((item) => item.name.toLowerCase().includes(trimmed.toLowerCase()))
              .map((item) => {
                const isCurrent = current.has(item.id);
                return (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    disabled={pending || isCurrent}
                    onSelect={() => !pending && !isCurrent && onSelect(item.id)}
                    data-testid={`${testIdPrefix}-option-${item.id}`}
                  >
                    {icon}
                    <span className="min-w-0 flex-1 truncate">{item.name}</span>
                    {isCurrent ? (
                      <span className="text-2xs text-muted-foreground">Current</span>
                    ) : null}
                    <span className="text-2xs text-muted-foreground">
                      {item.memberCount === 1 ? '1 agent' : `${item.memberCount} agents`}
                    </span>
                  </CommandItem>
                );
              })}
          </CommandGroup>
          {canCreate ? (
            <CommandGroup>
              <CommandItem
                value={`create:${trimmed}`}
                disabled={pending}
                onSelect={() => !pending && onCreate(trimmed)}
                data-testid={`${testIdPrefix}-create`}
              >
                <Plus className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">Create “{trimmed}”</span>
                <Check className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              </CommandItem>
            </CommandGroup>
          ) : null}
        </CommandList>
      </Command>
    </>
  );
}
